/**
 * Screen Skeleton Loaders
 * Lightweight placeholders for lazy-loaded screens
 */

import React from 'react';
import { cn } from '@/shared/lib/utils';

interface SkeletonProps {
  className?: string;
}

const Skeleton: React.FC<SkeletonProps & { variant?: 'rect' | 'circle' | 'text' }> = ({ 
  className, 
  variant = 'rect' 
}) => (
  <div 
    className={cn(
      "animate-pulse bg-muted",
      variant === 'circle' && "rounded-full",
      variant === 'text' && "rounded h-4",
      variant === 'rect' && "rounded-lg",
      className
    )} 
  />
);

/**
 * Home Screen Skeleton
 * Shows daily tasks grid and header placeholders
 */
export const HomeScreenSkeleton: React.FC = () => (
  <div className="min-h-screen bg-background p-4 pb-24">
    {/* Header */}
    <div className="flex items-center justify-between mb-6">
      <Skeleton className="w-32 h-8" />
      <Skeleton variant="circle" className="w-10 h-10" />
    </div>
    
    {/* Daily Progress Header */}
    <div className="bg-card rounded-2xl p-4 mb-6">
      <div className="flex items-center gap-4">
        <Skeleton variant="circle" className="w-16 h-16" />
        <div className="flex-1 space-y-2">
          <Skeleton variant="text" className="w-24" />
          <Skeleton variant="text" className="w-32" />
        </div>
        <Skeleton className="w-12 h-12 rounded-xl" />
      </div>
    </div>
    
    {/* Daily Tasks 2x2 Grid */}
    <div className="grid grid-cols-2 gap-4 mb-6">
      {[...Array(4)].map((_, i) => (
        <Skeleton key={i} className="h-24 rounded-2xl" />
      ))}
    </div>
    
    {/* Recent Photos */}
    <Skeleton className="h-16 rounded-xl" />
  </div>
);

/**
 * Camera Screen Skeleton
 * Shows viewfinder placeholder
 */
export const CameraScreenSkeleton: React.FC = () => (
  <div className="min-h-screen bg-black flex flex-col">
    {/* Viewfinder */}
    <div className="flex-1 flex items-center justify-center">
      <Skeleton variant="circle" className="w-24 h-24 bg-muted/20" />
    </div>
    
    {/* Category Bar */}
    <div className="p-4">
      <div className="flex gap-2 overflow-hidden">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="w-16 h-12 rounded-full bg-muted/30 flex-shrink-0" />
        ))}
      </div>
    </div>
    
    {/* Bottom Controls */}
    <div className="p-6 flex items-center justify-center gap-8">
      <Skeleton className="w-14 h-14 rounded-lg bg-muted/30" />
      <Skeleton variant="circle" className="w-20 h-20 bg-muted/30" />
      <Skeleton variant="circle" className="w-14 h-14 bg-muted/30" />
    </div>
  </div>
);

/**
 * Score Screen Skeleton
 * Shows score header and cards
 */
export const ScoreScreenSkeleton: React.FC = () => (
  <div className="min-h-screen bg-background p-4 pb-24">
    {/* Score Header */}
    <div className="bg-gradient-to-br from-primary/20 to-primary/5 rounded-3xl p-6 mb-6">
      <div className="flex items-center justify-center mb-4">
        <Skeleton variant="circle" className="w-20 h-20" />
      </div>
      <div className="flex justify-center">
        <Skeleton className="w-32 h-10 rounded-xl" />
      </div>
    </div>
    
    {/* Today's Focus */}
    <Skeleton className="h-24 rounded-2xl mb-4" />
    
    {/* Farm Health */}
    <div className="space-y-3">
      <Skeleton variant="text" className="w-28" />
      <div className="flex gap-2">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} variant="circle" className="w-8 h-8" />
        ))}
      </div>
    </div>
  </div>
);

/**
 * Agent Screen Skeleton
 * Shows chat interface placeholder
 */
export const AgentScreenSkeleton: React.FC = () => (
  <div className="min-h-screen bg-background flex flex-col">
    {/* Header */}
    <div className="p-4 border-b border-border flex items-center gap-3">
      <Skeleton variant="circle" className="w-10 h-10" />
      <Skeleton variant="text" className="w-32" />
    </div>
    
    {/* Messages */}
    <div className="flex-1 p-4 space-y-4">
      <div className="flex gap-2">
        <Skeleton variant="circle" className="w-8 h-8 flex-shrink-0" />
        <Skeleton className="h-16 w-3/4 rounded-2xl" />
      </div>
      <div className="flex justify-end">
        <Skeleton className="h-12 w-2/3 rounded-2xl" />
      </div>
      <div className="flex gap-2">
        <Skeleton variant="circle" className="w-8 h-8 flex-shrink-0" />
        <Skeleton className="h-24 w-4/5 rounded-2xl" />
      </div>
    </div>
    
    {/* Input */}
    <div className="p-4 border-t border-border">
      <Skeleton className="h-12 rounded-full" />
    </div>
  </div>
);

/**
 * Network Screen Skeleton
 * Shows feed placeholder
 */
export const NetworkScreenSkeleton: React.FC = () => (
  <div className="min-h-screen bg-background pb-24">
    {/* Tabs */}
    <div className="p-4 flex gap-2">
      {[...Array(3)].map((_, i) => (
        <Skeleton key={i} className="h-10 flex-1 rounded-full" />
      ))}
    </div>
    
    {/* Hero Card */}
    <div className="px-4 mb-4">
      <Skeleton className="h-32 rounded-2xl" />
    </div>
    
    {/* Feed Items */}
    <div className="px-4 space-y-3">
      {[...Array(4)].map((_, i) => (
        <Skeleton key={i} className="h-20 rounded-xl" />
      ))}
    </div>
  </div>
);

/**
 * Settings Screen Skeleton
 */
export const SettingsScreenSkeleton: React.FC = () => (
  <div className="min-h-screen bg-background p-4 pb-24">
    <Skeleton variant="text" className="w-24 h-8 mb-6" />
    
    <div className="space-y-3">
      {[...Array(6)].map((_, i) => (
        <Skeleton key={i} className="h-14 rounded-xl" />
      ))}
    </div>
  </div>
);

/**
 * Generic Screen Skeleton
 * Fallback for any screen
 */
export const GenericScreenSkeleton: React.FC = () => (
  <div className="min-h-screen bg-background p-4 pb-24">
    {/* Header */}
    <div className="flex items-center gap-3 mb-6">
      <Skeleton variant="circle" className="w-10 h-10" />
      <Skeleton variant="text" className="w-32" />
    </div>
    
    {/* Content blocks */}
    <div className="space-y-4">
      <Skeleton className="h-32 rounded-2xl" />
      <Skeleton className="h-24 rounded-xl" />
      <Skeleton className="h-24 rounded-xl" />
    </div>
  </div>
);

/**
 * Satellite Screen Skeleton
 */
export const SatelliteScreenSkeleton: React.FC = () => (
  <div className="min-h-screen bg-background">
    {/* Map placeholder */}
    <Skeleton className="h-64 w-full" />
    
    {/* Controls */}
    <div className="p-4 space-y-4">
      <Skeleton className="h-12 rounded-xl" />
      <div className="grid grid-cols-2 gap-3">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-20 rounded-xl" />
        ))}
      </div>
    </div>
  </div>
);

/**
 * Analytics Screen Skeleton
 */
export const AnalyticsScreenSkeleton: React.FC = () => (
  <div className="min-h-screen bg-background p-4 pb-24">
    <Skeleton variant="text" className="w-32 h-8 mb-6" />
    
    {/* Chart placeholder */}
    <Skeleton className="h-48 rounded-2xl mb-6" />
    
    {/* Stats grid */}
    <div className="grid grid-cols-2 gap-3">
      {[...Array(4)].map((_, i) => (
        <Skeleton key={i} className="h-24 rounded-xl" />
      ))}
    </div>
  </div>
);